/**
 * CasePaymentsPanel — Phase 4 Payments (G.19).
 *
 * Generate a Stripe payment link for a case using a preset amount (Lloyd's initial-case and
 * subsequent-billing lists) or a custom amount, with an optional internal note. Lists the links
 * already generated for the case with a copy-URL action.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PaymentApi, {
  TicketPaymentDto,
  PaymentsForbiddenError,
  OtrPaymentBlockedError,
  ManualPaymentMethod,
  MANUAL_PAYMENT_METHODS,
  paymentMethodLabel,
  isStripePayment,
} from '../../api/PaymentApi';
import {
  INITIAL_CASE_PRESETS_USD,
  SUBSEQUENT_BILLING_PRESETS_USD,
  formatUsd,
  formatCentsUsd,
} from '../../constants/paymentPresets';
import FeeSuggestions from './FeeSuggestions';

interface CasePaymentsPanelProps {
  Ticket_guid: string;
}

const CasePaymentsPanel: React.FC<CasePaymentsPanelProps> = ({ Ticket_guid }) => {
  const [links, setLinks] = useState<TicketPaymentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [amount, setAmount] = useState<string>(''); // whole dollars
  const [note, setNote] = useState<string>('');

  // OTR cases block a normal payment link (client already paid Off the Record). Once blocked, staff can
  // confirm this is a jury/appeal escalation and retry.
  const [otrBlockMsg, setOtrBlockMsg] = useState<string | null>(null);
  const [isEscalationFee, setIsEscalationFee] = useState(false);

  // Record a payment the firm already has (cash, Zelle, cheque, or one taken in Clio).
  const [recordOpen, setRecordOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [manualAmount, setManualAmount] = useState<string>(''); // whole dollars
  const [manualMethod, setManualMethod] = useState<ManualPaymentMethod>('cash');
  // Defaults to today; staff can back-date a payment taken earlier (e.g. a Clio backfill).
  const [manualReceivedAt, setManualReceivedAt] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [manualReference, setManualReference] = useState<string>('');
  const [manualNote, setManualNote] = useState<string>('');

  // G.17/G.18 — permissioned amount on record + pre-paid third-party flag.
  const [canViewAmount, setCanViewAmount] = useState(false);
  const [recordAmount, setRecordAmount] = useState<string>(''); // whole dollars
  const [prepaid, setPrepaid] = useState(false);
  const [savingAmount, setSavingAmount] = useState(false);
  const [feeRefreshKey, setFeeRefreshKey] = useState(0);

  const fetchAmount = useCallback(async () => {
    if (!Ticket_guid) return;
    try {
      const data = await PaymentApi.getPaymentAmount(Ticket_guid);
      setCanViewAmount(true);
      setRecordAmount(
        data.Ticket_PaymentAmountCents != null ? String(data.Ticket_PaymentAmountCents / 100) : '',
      );
      setPrepaid(data.Ticket_PrepaidThirdParty);
    } catch (err) {
      if (err instanceof PaymentsForbiddenError) setCanViewAmount(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchAmount();
  }, [fetchAmount]);

  const handleSaveAmount = async () => {
    setSavingAmount(true);
    setError(null);
    try {
      const dollarsVal = recordAmount.trim() === '' ? null : Number(recordAmount);
      if (dollarsVal != null && (!Number.isFinite(dollarsVal) || dollarsVal < 0)) {
        setError('Enter a valid amount, or leave it blank.');
        return;
      }
      const cents = dollarsVal == null ? null : Math.round(dollarsVal * 100);
      await PaymentApi.setPaymentAmount(Ticket_guid, cents, prepaid);
      setSuccess('Payment amount saved.');
      // The saved amount feeds the appeal credit, so the suggestions below are now out of date.
      setFeeRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save amount');
    } finally {
      setSavingAmount(false);
    }
  };

  const fetchLinks = useCallback(async () => {
    if (!Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      setLinks(await PaymentApi.listPaymentLinks(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const dollars = Number(amount);
  const amountValid = Number.isFinite(dollars) && dollars >= 0.5 && dollars <= 50000;

  const handleCreate = async () => {
    if (!amountValid) {
      setError('Enter an amount between $0.50 and $50,000.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const amountCents = Math.round(dollars * 100);
      await PaymentApi.createPaymentLink(Ticket_guid, amountCents, note, isEscalationFee);
      setSuccess(`Payment link created for ${formatUsd(dollars)}.`);
      setAmount('');
      setNote('');
      setOtrBlockMsg(null);
      setIsEscalationFee(false);
      await fetchLinks();
    } catch (err) {
      if (err instanceof OtrPaymentBlockedError) {
        // Reveal the escalation confirmation rather than a dead-end error.
        setOtrBlockMsg(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create payment link');
      }
    } finally {
      setCreating(false);
    }
  };

  const manualDollars = Number(manualAmount);
  const manualAmountValid =
    Number.isFinite(manualDollars) && manualDollars > 0 && manualDollars <= 50000;

  const handleRecordPayment = async () => {
    if (!manualAmountValid) {
      setError('Enter an amount between $0.01 and $50,000.');
      return;
    }
    setRecording(true);
    setError(null);
    try {
      await PaymentApi.recordManualPayment(Ticket_guid, {
        amountCents: Math.round(manualDollars * 100),
        method: manualMethod,
        // Send a full timestamp so the server stores a real instant, not a midnight-UTC edge case.
        receivedAt: manualReceivedAt
          ? new Date(`${manualReceivedAt}T12:00:00`).toISOString()
          : undefined,
        reference: manualReference,
        note: manualNote,
      });
      setSuccess(
        `Recorded ${formatUsd(manualDollars)} paid by ${paymentMethodLabel(manualMethod)}.`,
      );
      setManualAmount('');
      setManualReference('');
      setManualNote('');
      setRecordOpen(false);
      // The case total feeds the appeal credit, so the suggestions above are now stale.
      setFeeRefreshKey((k) => k + 1);
      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setRecording(false);
    }
  };

  const handleVoid = async (payment: TicketPaymentDto) => {
    const reason = window.prompt(
      `Void this ${formatCentsUsd(payment.TicketPayment_AmountCents)} payment? It stays on the record for audit but stops counting toward the case total.\n\nReason (optional):`,
    );
    // prompt returns null when cancelled; an empty string is a deliberate "no reason given".
    if (reason === null) return;
    setError(null);
    try {
      await PaymentApi.voidPayment(Ticket_guid, payment.TicketPayment_guid, reason);
      setSuccess('Payment voided.');
      setFeeRefreshKey((k) => k + 1);
      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void payment');
    }
  };

  const handleCopy = async (url: string | null) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('Link copied to clipboard.');
    } catch {
      setError('Could not copy the link.');
    }
  };

  const renderPresetRow = (label: string, presets: number[]) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {presets.map((p) => (
          <Chip
            key={`${label}-${p}`}
            label={formatUsd(p)}
            size="small"
            variant={Number(amount) === p ? 'filled' : 'outlined'}
            color={Number(amount) === p ? 'primary' : 'default'}
            onClick={() => setAmount(String(p))}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      {canViewAmount && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Amount on record
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
            <TextField
              label="Case amount (USD)"
              type="number"
              size="small"
              value={recordAmount}
              onChange={(e) => setRecordAmount(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              sx={{ maxWidth: 200 }}
            />
            <FormControlLabel
              control={<Checkbox checked={prepaid} onChange={(e) => setPrepaid(e.target.checked)} />}
              label="Pre-paid by third party (no charge)"
            />
            <Button variant="outlined" onClick={handleSaveAmount} disabled={savingAmount}>
              {savingAmount ? 'Saving…' : 'Save amount'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Visible only to staff with payment access. Pre-paid third-party cases (CDL Consultants /
            CDL Legal / OTR) record the amount without charging.
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      <FeeSuggestions
        Ticket_guid={Ticket_guid}
        onUseAmount={setAmount}
        refreshKey={feeRefreshKey}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Create a payment link
      </Typography>

      {renderPresetRow('Initial case', INITIAL_CASE_PRESETS_USD)}
      {renderPresetRow('Subsequent billing', SUBSEQUENT_BILLING_PRESETS_USD)}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, mb: 1 }}>
        <TextField
          label="Amount (USD)"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0.5, max: 50000, step: 1 }}
          sx={{ maxWidth: 200 }}
        />
        <TextField
          label="Note (optional — internal)"
          size="small"
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for a custom charge (kept internal)"
        />
      </Stack>

      {otrBlockMsg && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {otrBlockMsg}
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Checkbox
                checked={isEscalationFee}
                onChange={(e) => setIsEscalationFee(e.target.checked)}
              />
            }
            label="This is a jury trial / county appeal fee — bill the client directly"
          />
        </Alert>
      )}

      <Button
        variant="contained"
        sx={{ mt: otrBlockMsg ? 1 : 0 }}
        onClick={handleCreate}
        disabled={creating || !amountValid || (otrBlockMsg !== null && !isEscalationFee)}
        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {creating ? 'Creating…' : otrBlockMsg ? 'Create escalation payment link' : 'Create payment link'}
      </Button>

      <Divider sx={{ my: 2 }} />

      {/*
        Record money the firm already has. Separate from the link flow above on purpose: that one
        asks a client to pay, this one states that they already did (Lloyd, Jul 29 2026).
      */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Payment received another way
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => setRecordOpen((open) => !open)}>
          {recordOpen ? 'Cancel' : 'Record a payment'}
        </Button>
      </Box>

      {recordOpen && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            For money taken outside this site — cash, Zelle, a check, or a payment already made in
            Clio. It counts toward the case total straight away.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Amount (USD)"
              type="number"
              size="small"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              inputProps={{ min: 0.01, max: 50000, step: 1 }}
              sx={{ maxWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="manual-method-label">Paid by</InputLabel>
              <Select
                labelId="manual-method-label"
                label="Paid by"
                value={manualMethod}
                onChange={(e) => setManualMethod(e.target.value as ManualPaymentMethod)}
              >
                {MANUAL_PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {paymentMethodLabel(method)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Date received"
              type="date"
              size="small"
              value={manualReceivedAt}
              onChange={(e) => setManualReceivedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              sx={{ maxWidth: 190 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Reference (optional)"
              size="small"
              value={manualReference}
              onChange={(e) => setManualReference(e.target.value)}
              placeholder="Check no. / Zelle confirmation / Clio id"
              sx={{ minWidth: 260 }}
            />
            <TextField
              label="Note (optional — internal)"
              size="small"
              fullWidth
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
            />
          </Stack>

          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={recording || !manualAmountValid}
            startIcon={recording ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {recording ? 'Recording…' : 'Record payment'}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Payments ({links.length})
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : links.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
          No payments or payment links on this case yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {links.map((link) => {
            const voided = link.TicketPayment_Status === 'voided';
            const paid = link.TicketPayment_Status === 'paid';
            // Only a hand-entered payment can be voided here — a Stripe row is corrected by
            // refunding in Stripe, so we do not offer an action that would desync the two.
            const canVoid = !voided && !isStripePayment(link.TicketPayment_Method);

            return (
              <Card
                key={link.TicketPayment_guid}
                variant="outlined"
                sx={voided ? { opacity: 0.6 } : undefined}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textDecoration: voided ? 'line-through' : 'none',
                      }}
                    >
                      {formatCentsUsd(link.TicketPayment_AmountCents)}
                    </Typography>
                    <Chip
                      label={voided ? 'voided' : paid ? 'paid' : 'link created'}
                      size="small"
                      variant="outlined"
                      color={voided ? 'error' : paid ? 'success' : 'default'}
                    />
                    <Chip
                      label={paymentMethodLabel(link.TicketPayment_Method)}
                      size="small"
                      variant="outlined"
                    />
                    <Box sx={{ flex: 1 }} />
                    {link.TicketPayment_Url && (
                      <>
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => handleCopy(link.TicketPayment_Url)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open link">
                          <IconButton
                            size="small"
                            component={MuiLink}
                            href={link.TicketPayment_Url}
                            target="_blank"
                            rel="noopener"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {canVoid && (
                      <Button size="small" color="error" onClick={() => handleVoid(link)}>
                        Void
                      </Button>
                    )}
                  </Box>
                  {link.TicketPayment_Reference && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Ref: {link.TicketPayment_Reference}
                    </Typography>
                  )}
                  {link.TicketPayment_Note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-line' }}
                    >
                      {link.TicketPayment_Note}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                    {link.TicketPayment_CreatedByName || 'Staff'} ·{' '}
                    {link.TicketPayment_ReceivedAt
                      ? `received ${new Date(link.TicketPayment_ReceivedAt).toLocaleDateString()}`
                      : `created ${new Date(link.TicketPayment_CreatedAt).toLocaleString()}`}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CasePaymentsPanel;
